import { useMemo, useState } from 'react'

import { isTopPartner } from 'partner360/features/welcomeFlow/lib/isTopPartner'
import { IWelcomeFlowPartner } from 'partner360/features/welcomeFlow/model/types'

/** The filters of the Welcome Flow's partner list, with the same defaults and the same order. */
export const useMyPartnersFilters = (partners: IWelcomeFlowPartner[]) => {
  const [searchString, setSearchString] = useState('')
  const [onlyHeadquarters, setOnlyHeadquarters] = useState(true)
  const [onlyTopPartners, setOnlyTopPartners] = useState(false)
  const [onlyRequests, setOnlyRequests] = useState(false)

  const filteredPartners = useMemo(() => {
    let result = partners

    if (onlyRequests) {
      result = result.filter((partner) => partner.STRequestAction !== undefined)
    }

    if (onlyHeadquarters) {
      result = result.filter((partner) => partner.HierarchyType === 'Headquarters')
    }

    if (onlyTopPartners) {
      result = result.filter((partner) => isTopPartner(partner))
    }

    if (searchString.trim()) {
      const searchLower = searchString.toLowerCase()
      result = result.filter(
        (partner) =>
          partner.PartnerId.toLowerCase().includes(searchLower) ||
          partner.LocationId.toLowerCase().includes(searchLower) ||
          partner.PartnerName.toLowerCase().includes(searchLower)
      )
    }

    return result
  }, [partners, searchString, onlyRequests, onlyHeadquarters, onlyTopPartners])

  const counts = useMemo(
    () => ({
      headquarters: filteredPartners.filter((partner) => partner.HierarchyType === 'Headquarters')
        .length,
      topPartners: filteredPartners.filter((partner) => isTopPartner(partner)).length,
      requests: filteredPartners.filter((partner) => partner.STRequestAction !== undefined).length,
    }),
    [filteredPartners]
  )

  return {
    searchString,
    setSearchString,
    onlyHeadquarters,
    setOnlyHeadquarters,
    onlyTopPartners,
    setOnlyTopPartners,
    onlyRequests,
    setOnlyRequests,
    filteredPartners,
    counts,
  }
}

export type TMyPartnersFilters = ReturnType<typeof useMyPartnersFilters>
